import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import ShapeModel from './shape-model';
import * as mobx from 'mobx';
import {
    TDWActionType, TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { transaction } from '@src/slide/domain/slide-app';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';
import _set from 'lodash/set';
import ResizeMeasureCanvas from '@src/slide/domain/ui-model/element-modeles/property/text/editor-measure/resize-measure';
import ElementBaseModel from '@src/slide/domain/ui-model/element-modeles/base-modeles/element-base-model';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import {
    IRowColStartEnd, ITableModel, ITableCellModel,
    ICollect, IBorder, IResizeInfo, IRange, IRowCol, IInsertRange, ITblProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { ITableProps } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/table-model-props.interface';
import { IBatchMeasureBlockSizeProps } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-common.interface';
import { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { ISlideBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { ITextEditor } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/text-editor.interface';
import { getSlideIndex } from '@src/slide/domain/ui-model/element-modeles/property/slide-number/operation';

import TableBorder from '@src/slide/domain/ui-model/element-modeles/property/border/table-border';
import { ITableBorderProperty } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { cloneDeep } from '@tencent/slide-sdk-model/src/utils/clone-deep';
import { TextConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-converter';
import {
    DeltaOf, ICTTextBody, ITableData, ITableDataModel, IHeightInfo, DeleteDirection, InsertDirection,
    ITableRangeData, IWidthInfo, DataType, TableDataModel, DataModelChange, ICTTableCell,
} from '@tencent/slide-sdk-model';
import { minColWidth } from '@src/slide/domain/ui-model/element-modeles/config/default-data/table-default';
import { TablePropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/table-property-converter';
import { IShapeModel, ITextPaddingAdjust } from './Interface/shape-model.interface';
import AdjustTableCellBg from './property/fill/adjust-table-cell-bg';
import { getChangeSetType } from '../utils/changeset-type';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { TableRefreshMenu } from './property/menu-property/refresh/table';
import { getChangedPart } from '@src/slide/infrastructure/module/independent/object/object-path';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { changeArrAppendToSetOrModify } from '@src/slide/infrastructure/module/independent/object/arr-append-to-set-or-modify';
import ModelConverterBridge from '@src/slide/domain/ui-model/element-modeles/convert/model-converter-bridge';
import { ICTTextBodyPropertiesDelta } from '@tencent/slide-sdk-pb-model';
import { IFillProperty } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import Fill from '@src/slide/domain/ui-model/element-modeles/property/fill';

const tableRowListPath = 'graphic.graphicData.tbl.tr';
const tableColListPath = 'graphic.graphicData.tbl.tblGrid.gridCol';
const tableRowListColPath = 'tc';
const textBodyPath = 'txBody';

class TableModel extends ElementBaseModel implements ITableModel {
    /**
     * 将sdkModel转换为uiModel
     * @param sdkData 合并了继承属性的sdkModel
     * @param uiData 当前uiModel
     * @param rawSdkData 原始sdkModel
     * @param changesetInfo 附加参数，sdk的observers回调为源头
     */
    // eslint-disable-next-line complexity
    public static async convertDataSdkToUi(
        sdkData: ITableDataModel,
        uiData: ITableModel,
        rawSdkData: TableDataModel,
        changesetInfo?: DataModelChange | null,
    ): Promise<void> {
        if (_isEqual(uiData, {})) return;
        if (!sdkData) {
            return;
        }
        uiData.resetUpdateState();
        super.convertDataSdkToUi(sdkData, uiData, rawSdkData);

        const reuseItems = TableModel.getReuseItems(uiData);

        const oldRowCount = uiData.props.rowHeights.length;
        const oldColCount = uiData.props.colWidths.length;

        const convertPromises: Array<Promise<any> | IBaseModel> = [];
        const convertTableTask = ModelConverterBridge.convertSdkToUi({
            type: UIModelType.TABLE,
            sdkData,
            uiData,
        });
        convertPromises.push(convertTableTask);

        const items: ITableCellModel[][] = [];
        const sdkStoreData = (rawSdkData as unknown as TableDataModel).store.data;
        // 此处缓存changeSet操作类型，供cellModel使用
        uiData.csType = getChangeSetType(changesetInfo);
        // 基于性能考虑，此处采用sdkData.data，直接用sdkData，下方读取属性值时，slide-sdk会有深拷贝
        const data = sdkData.props;
        const sdkTblRowList = _get(data, tableRowListPath);
        for (let row = 0; row < sdkTblRowList.length; row++) {
            const rowList: ITableCellModel[] = [];
            const rowInfo = sdkTblRowList[row];
            for (let col = 0; col < rowInfo[tableRowListColPath].length; col++) {
                const cellInfo = rowInfo[tableRowListColPath][col];
                const refSdkData = _get(sdkStoreData, tableRowListPath)[row][tableRowListColPath][col];
                const shape = TableModel.getCellModel(cellInfo, refSdkData, uiData, reuseItems);

                TableModel.setCellWidth(uiData, col, cellInfo.gridSpan, shape);

                _set(shape, 'col', col);
                _set(shape, 'row', row);

                if (uiData.changeSetEngine.hasChangeAtPath(`${tableRowListPath}.${row}.${tableRowListColPath}.${col}`)) {
                    shape.changeSetEngine.changesetInfo = uiData.changeSetEngine.changesetInfo;
                    const convertCellPromise = shape.doConvertSdkDataModelToUiData(cellInfo, changesetInfo);
                    convertPromises.push(convertCellPromise);
                }
                rowList.push(shape);
            }
            items.push(rowList);
        }
        uiData.csType = undefined;

        if (uiData.parent?.asyncConvertChildren) {
            await Promise.all(convertPromises);
            _set(uiData, 'asyncConvertChildren', false);
        }
        if (oldRowCount !== uiData.props.rowHeights.length || oldColCount !== uiData.props.colWidths.length) {
            // 行列数发生变化时，表格需要更新
            _set(uiData, 'shouldUpdate', true);
        }
        _set(uiData, 'props.items', items);
        _set(uiData, 'tableItems', items);
    }

    /**
     * sdk转ui后的操作
     * @param sdkData 合并了继承属性的sdkModel
     * @param uiData 当前uiModel
     * @param rawSdkData 原始sdkModel
     * @param changesetInfo 附加参数，sdk的observers回调为源头
     */
    public static didConvertDataSdkToUi(
        sdkData: ITableDataModel,
        uiData: ITableModel,
        rawSdkData: ITableDataModel,
        changesetInfo?: DataModelChange | null,
    ): void {
        super.didConvertDataSdkToUi(sdkData, uiData, rawSdkData);

        // 通过sdk原始数据与继承后的对比，将自有的属性打标，用来做数据提交时剔除继承属性
        const ownSdkTable = sdkData.getOwnedData();
        ownSdkTable?.graphic?.graphicData?.tbl?.tr?.forEach((tr, trIndex) => {
            tr?.tc?.forEach((tc, tcIndex) => {
                TextConverter.sdkToUiSignRawTextProps(
                    tc?.txBody as ICTTextBody,
                    uiData.tableItems?.[trIndex]?.[tcIndex],
                );
            });
        });

        if (!uiData.selectedRange) {
            if (uiData.defaultSelectedRange) {
                _set(uiData, 'selectedRange', uiData.defaultSelectedRange);
                _set(uiData, 'defaultSelectedRange', null);
            } else {
                _set(uiData, 'selectedRange', null);
            }
        } else if (uiData.selectedRange.rowEnd >= uiData.props.rowHeights.length
            || uiData.selectedRange.colEnd >= uiData.props.colWidths.length) {
            // 选中单元格范围发生异常时选中整个表格
            _set(uiData, 'showSelection', false);
            _set(uiData, 'selectedRange', {
                rowStart: 0,
                colStart: 0,
                rowEnd: uiData.props.rowHeights.length - 1,
                colEnd: uiData.props.colWidths.length - 1,
            });
        }
        _set(uiData, 'selectedCellRanges', uiData.calculatSelectedCells(uiData.selectedRange));
        if (typeof uiData.updateTableInfo === 'function') {
            uiData.updateTableInfo();
        }
        TableRefreshMenu.caculateItemValues(uiData);
        uiData.saveState?.({ shouldUpdate: true });
        uiData.shouldUpdate = true;
        new AdjustTableCellBg().adjustTableBg(uiData, changesetInfo);
    }

    /**
     * 将ui数据转换为sdk类型
     * @param uiData
     * @param sdkData
     * @param rawUiData
     */
    public static convertDataUiToSdk(uiData: any, sdkData: any, rawUiData: any): void {
        ModelConverterBridge.convertUiToSdk({ type: UIModelType.TABLE, uiData, sdkData, rawUiData });
    }

    /**
     * 生成单元格uiModel
     * @param cellInfo 合并了继承属性的sdkCellModel
     * @param refSdkData 原始sdkCellModel
     * @param uiData 当前表格uiData
     * @param reuseItems 原始单元格二维数组
     */
    public static getCellModel(
        cellInfo: ICTTableCell,
        refSdkData: ICTTableCell,
        uiData: ITableModel,
        reuseItems: ITableCellModel[][],
    ): ITableCellModel {
        const reuseShape = TableModel.getReuseModel(reuseItems, refSdkData, uiData);
        let newShape;
        if (!reuseShape) {
            newShape = new ShapeModel();
            newShape.parent = uiData;
            newShape.sdkData = refSdkData;
            newShape.selectionService = uiData.selectionService;
            newShape.tableModelRef = uiData;
            newShape.cellInfo = cellInfo;
            _set(newShape, 'inTable', true);
            _set(newShape, 'props.style.border.width', 0);
        }
        return reuseShape || newShape;
    }

    /**
     * 获取单元格二维数组
     * @param uiData
     */
    public static getReuseItems(uiData: ITableModel): ITableCellModel[][] {
        let reuseItems: ITableCellModel[][] = [];
        while (uiData.tableItems && uiData.tableItems.length > 0) {
            reuseItems = reuseItems.concat(uiData.tableItems[0]);
            uiData.tableItems.splice(0, 1);
        }
        return reuseItems;
    }

    /**
     * 给单元格设置宽度
     * @param uiData
     * @param col
     * @param colspan
     * @param shape
     */
    public static setCellWidth(uiData: ITableModel, col: number, colspan: number, shape: ITableCellModel): void {
        const displayWidth = uiData.cellWidth(col, colspan || 1);
        if (displayWidth !== _get(shape, 'props.style.width')) {
            _set(shape, 'props.style.width', displayWidth);
            _set(shape, 'cellWidthChanged', true);
            _set(uiData, 'shouldUpdate', true);
        }
    }

    /**
     * 3.0sdk更新文本
     * @param uiSdk
     * @param txBody
     * @param colIndex
     * @param rowIndex
     * @private
     */
    public static sdkV3UpdateCellParagraphList(
        uiSdk: TableDataModel,
        txBody: ICTTextBody,
        colIndex: number,
        rowIndex: number,
    ): void {
        const oldTxBody = uiSdk.store.data.graphic?.graphicData?.tbl?.tr?.[rowIndex]?.tc?.[colIndex]?.txBody;
        // 包含无用的set，剔除未变的属性
        const changedTxBody = getChangedPart(oldTxBody, txBody);
        if (!Object.keys(changedTxBody || {}).length) {
            return;
        }
        uiSdk.api.transaction(() => {
            if (changedTxBody.p) {
                uiSdk.api.updateParagraphList(changedTxBody.p, colIndex, rowIndex);
            }
            if (changedTxBody.bodyPr && Object.keys(changedTxBody.bodyPr)) {
                // 表格垂直方式这种文本框属性，优先保存在了单元格的tcPr上，为了兼容这些特殊场景，在更新文本的bodyPr时，同时更新单元格的tcPr
                changeArrAppendToSetOrModify(changedTxBody.bodyPr);
                uiSdk.api.updateTextBodyProperties({ bodyPr: changedTxBody.bodyPr, col: colIndex, row: rowIndex });
                const range = { startRowIndex: rowIndex, startColIndex: colIndex, rowSpan: 1, colSpan: 1 };
                uiSdk.api.updateTableCellProperties(changedTxBody.bodyPr, range);
            }
        });
    }

    private static getReuseModel(models: Array<any>, sdkData: any, parent: ITableModel) {
        let model: IShapeModel | null = null;
        for (let index = 0; index < models.length; index++) {
            const uiModel = models[index];
            const existSdk = uiModel.sdkData || uiModel.sdkDataRef;
            if (existSdk === sdkData) {
                model = models[index] as IShapeModel;
                model.parent = parent;
                models.splice(index, 1);
                break;
            }
        }
        return model;
    }

    declare public sdkData: ITableDataModel | null;
    public props: ITableProps;
    public autoFocus = false;
    public showSelection = false;
    public selectedCellRanges: IRowColStartEnd[] = [];
    public shouldUpdate = false;
    public tableItems: ITableCellModel[][] = [];
    public tableBorders: { [key: string]: any } = {};
    public defaultSelectedRange: IRowColStartEnd | null  = null;
    @mobx.observable public selectedRange: IRowColStartEnd | null = null;
    @mobx.observable public displayRowHeights: number[] = [];

    // 标题行
    public firstRow = true;

    // 汇总行
    public lastRow = false;

    // 第一列
    public firstCol = false;

    // 最后一列
    public lastCol = false;

    // 镶边行
    public bandRow = true;

    // 镶边列
    public bandCol = false;
    public tableStyleId: string | undefined;
    private readonly defaultTableWidth = ptToCanvas(800);
    private localChangeSet?: boolean;
    private tableBorderInstance: ITableBorderProperty;

    public get isEditing(): boolean {
        if (this.selectedCellRanges.length === 1) {
            const range = this.selectedCellRanges[0];
            const shape = this.tableItems[range.rowStart][range.colStart];
            return !!shape.editorInstance?.isEditing;
        }
        return false;
    }

    public get editorInstance(): ITextEditor | null {
        if (this.selectedCellRanges.length === 1) {
            const range = this.selectedCellRanges[0];
            const shape = this.tableItems[range.rowStart][range.colStart];
            return shape.editorInstance || null;
        }
        return null;
    }

    public constructor() {
        super();
        this.type = UIModelType.TABLE;
        this.props = {
            style: {
                left: 0,
                top: 0,
                width: 0,
                height: 0,
                opacity: 1,
                position: 'absolute',
            },
            rowHeights: [],
            colWidths: [],
            items: [],
        };
    }

    /**
     * 单元格聚焦
     * @param row 行号
     * @param col 列号
     */
    public enterTouchCell = (row: number, col: number): void => {
        this.showSelection = false;
        this.autoFocus = true;
        this.afterSelection(row, col, row, col);
        this.autoFocusSelectedCell();
    };

    /**
     * 聚焦第一个单元格
     */
    public enterFirstCell = (): void => {
        this.showSelection = false;
        this.autoFocus = true;
        this.afterSelection(0, 0, 0, 0);
        this.autoFocusSelectedCell();
    };

    /**
     * 单元格宽度
     * @param col 列号
     * @param colspan 跨越的列数
     */
    public cellWidth = (col: number, colspan: number): number => {
        let width = 0;
        for (let i = col; i < col + colspan; i++) {
            width += this.props.colWidths[i];
        }
        return width;
    };

    /**
     * 单元格高度
     * @param row 行号
     * @param rowspan 跨越的行数
     */
    public cellHeight = (row: number, rowspan: number): number => {
        let height = 0;
        for (let i = row; i < row + rowspan; i++) {
            height += this.displayRowHeights[i];
        }
        return height;
    };

    public averageTableColWidth = () => {
        if (!this.selectedRange) return;
        const { colEnd, colStart } = this.selectedRange;
        const { rowEnd, rowStart } = this.selectedRange;
        const colLength = this.tableItems?.[0]?.length || 0;
        const rowLength = this.tableItems?.length || 0;

        let isSelectedAll = false;
        if ((colEnd - colStart) === 0 && (rowEnd - rowStart === 0)) {
            isSelectedAll = true;
        }

        const options = isSelectedAll ? isSelectedAll && {
            colStart: 0,
            colEnd: colLength - 1,
            rowStart: 0,
            rowEnd: rowLength - 1,
        } : undefined;

        const widthStart = isSelectedAll ? 0 : colStart;
        const widthEnd = isSelectedAll ? colLength : colEnd + 1;

        const widths = this.props.colWidths.slice(widthStart, widthEnd).reduce((a, b) => a + b);
        const averageColWidth = widths / (widthEnd - widthStart);
        this.resizeSelectedMultiCol(averageColWidth, options);
    };

    public averageTableRowHeight = () => {
        if (!this.selectedRange) return;
        const { rowEnd, rowStart } = this.selectedRange;
        const { colEnd, colStart } = this.selectedRange;
        const colLength = this.tableItems[0].length;
        const rowLength = this.tableItems.length;

        let isSelectedAll = false;
        if ((colEnd - colStart) === 0 && (rowEnd - rowStart === 0)) {
            isSelectedAll = true;
        }

        const options = isSelectedAll ? isSelectedAll && {
            colStart: 0,
            colEnd: colLength - 1,
            rowStart: 0,
            rowEnd: rowLength - 1,
        } : undefined;

        const heightStart = isSelectedAll ? 0 : rowStart;
        const heightEnd = isSelectedAll ? rowLength : rowEnd + 1;
        const heights = this.props.rowHeights.slice(heightStart, heightEnd).reduce((a, b) => a + b);
        let averageRowHeight = heights / (heightEnd - heightStart);

        this.props.rowHeights.forEach((height, index) => {
            if (index >= heightStart && heightStart < heightEnd) {
                const textHeights = this.measureRowCellsHeight(index);
                textHeights.forEach((textHeight) => {
                    averageRowHeight = Math.max(averageRowHeight, textHeight);
                });
            }
        });

        this.resizeSelectedMultiRow(averageRowHeight, options);
    };

    public resizeSelectedMultiCol = (newSize: number, selectedRange?: IRowColStartEnd | null) => {
        this.uploadEditingCellData(false);
        const updateWidth = Math.ceil(newSize);
        const newColWidths = [...this.props.colWidths];
        const { colStart = 0, colEnd = 0 } = selectedRange ?? (this.selectedRange || {});
        const style: any = {
            width: this.props.style.width,
        };
        let isChange = false;
        for (let index = colStart; index <= colEnd; index++) {
            const originWidth = Math.ceil(newColWidths[index]);
            if (originWidth !== updateWidth) {
                isChange = true;
                if (isChange) {
                    const changeWidth = updateWidth - originWidth;
                    style.width = style.width + changeWidth;
                    newColWidths[index] = updateWidth;
                }
            }
        }

        if (!isChange) return;
        const sdkDataV3 = this.sdkData as unknown as ITableDataModel;
        const uiData = { props: { style, colWidths: newColWidths } };
        const sdkData: DeltaOf<ITableData> = {};
        TablePropertyConverter.convertPartialDataFromUiToSdk(uiData, sdkData, this);
        const transform = sdkData.spPr?.xfrm;
        const widths = sdkData.graphic?.graphicData?.tbl?.tblGrid?.gridCol
            ?.map((widthObj, index) => ({ index, width: widthObj.w }))
            ?.filter(e => e.width !== undefined);
        if (transform) {
            sdkDataV3.api.updateTransform(transform);
        }
        if (widths) {
            sdkDataV3.api.setTableColWidths(widths as IWidthInfo[]);
        }
    };

    public getBackgroundFill(): IFillProperty {
        if (!this.fillInstance) {
            this.fillInstance = new Fill(this);
        }
        return this.fillInstance;
    }

    public clearAllCellFill = () => {
        transaction(() => {
            this.selectedRange = {
                rowStart: 0,
                colStart: 0,
                rowEnd: this.props.rowHeights.length - 1,
                colEnd: this.props.colWidths.length - 1,
            };
            this.getBackgroundFill().updateCurrentFillForCurrentModel({});
            this.getBorder().clearTableBorderStyle();
        });
    };

    public updateTblProp = (props: ITblProps) => {
        const { firstRow, firstCol, lastRow, lastCol, bandRow, bandCol, tableStyleId } = props;
        const tblPr = {
            firstRow: firstRow ?? this.firstRow,
            firstCol: firstCol ?? this.firstCol,
            lastRow: lastRow ?? this.lastRow,
            lastCol: lastCol ?? this.lastCol,
            bandRow: bandRow ?? this.bandRow,
            bandCol: bandCol ?? this.bandCol,
            tableStyleId: tableStyleId ?? this.tableStyleId,
        };

        transaction(() => {
            this.sdkData?.api.updateProps({
                graphic: {
                    graphicData: {
                        tbl: {
                            tblPr,
                        },
                    },
                },
            });
        });
    };

    public adjustCellFontPaddingWithSelectRange = (
        values: Partial<ITextPaddingAdjust>,
        selectedRange?: IRowColStartEnd | null,
    ) => {
        const updateTextBodyStyle = this.getFixedCellPaddingProps(values);

        const { rowStart = 0, rowEnd = 0, colEnd = 0, colStart = 0 } = selectedRange ?? (this.selectedRange || {});
        transaction(() => {
            for (let rowIndex = rowStart; rowIndex <= rowEnd; rowIndex++) {
                for (let colIndex = colStart; colIndex <= colEnd; colIndex++) {
                    const cellModel = this.tableItems[rowIndex][colIndex];
                    cellModel.textBodyStyle = {
                        ...cellModel.textBodyStyle,
                        ...updateTextBodyStyle,
                    };

                    const sdkData: {
                        bodyPr?: DeltaOf<ICTTextBodyPropertiesDelta>
                    } = {};
                    TextConverter.convertPartialDataFromUiToSdk(cellModel, sdkData);
                    this.sdkData?.api.updateTextBodyProperties({
                        bodyPr: sdkData.bodyPr,
                        row: rowIndex,
                        col: colIndex,
                    });
                }
            }
        });
    };

    public getFixedCellPaddingProps(values: Partial<ITextPaddingAdjust>) {
        const { paddingLeft, paddingRight, paddingTop, paddingBottom } = values;
        const updateTextBodyStyle = {};
        const padding: any = {};
        if (paddingLeft !== undefined && !isNaN(paddingLeft)) {
            padding.paddingLeft = paddingLeft;
        }
        if (paddingRight !== undefined && !isNaN(paddingRight)) {
            padding.paddingRight = paddingRight;
        }
        if (paddingTop !== undefined && !isNaN(paddingTop)) {
            padding.paddingTop = paddingTop;
        }
        if (paddingBottom !== undefined && !isNaN(paddingBottom)) {
            padding.paddingBottom = paddingBottom;
        }

        // 更新textBodyStyle， 用于重新计算容器形状
        Object.keys(padding).forEach((key) => {
            const value = padding[key];
            _set(updateTextBodyStyle, `bodyStyle.${key}`, value);
        });

        return updateTextBodyStyle;
    }

    public resizeSelectedMultiRow = (newSize: number, selectedRange?: IRowColStartEnd | null) => {
        this.uploadEditingCellData(false);
        const updateHeight = Math.ceil(newSize);
        const newRowHeights = [...this.props.rowHeights];
        const { rowStart = 0, rowEnd = 0 } = selectedRange ?? (this.selectedRange || {});
        const style: any = {
            height: this.props.style.height,
        };
        let isChange = false;
        for (let index = rowStart; index <= rowEnd; index++) {
            const originWidth = Math.ceil(newRowHeights[index]);
            if (originWidth !== updateHeight) {
                isChange = true;
                if (isChange) {
                    const changeWidth = updateHeight - originWidth;
                    style.width = style.width + changeWidth;
                    newRowHeights[index] = updateHeight;
                }
            }
        }

        if (!isChange) return;
        const sdkDataV3 = this.sdkData as unknown as ITableDataModel;
        sdkDataV3.api.transaction(() => {
            const uiData = { props: { style, rowHeights: newRowHeights } };
            const sdkData: DeltaOf<ITableData> = {};
            TablePropertyConverter.convertPartialDataFromUiToSdk(uiData, sdkData, this);
            const transform = sdkData.spPr?.xfrm;
            const heights = sdkData.graphic?.graphicData?.tbl?.tr
                ?.map((obj, index) => ({ index, height: obj?.h }))
                ?.filter(e => e.height !== undefined);
            if (transform) {
                sdkDataV3.api.updateTransform(transform);
            }
            if (heights) {
                sdkDataV3.api.setTableRowHeights(heights as IHeightInfo[]);
            }
        });
    };

    /**
     * 调整列宽
     * @param col 变更宽度的目标列
     * @param newSize 目标列的目标宽度
     * @param isLeftEdge 是否为左边缘（不等同于col === 0的原因是 拖拽最左边以及最左边+1的线，变更的列都是第0列）
     */
    public resizeCol = (col: number, newSize: number, isLeftEdge: boolean): void => {
        this.uploadEditingCellData(false);
        const colWidth = Math.ceil(newSize);
        // 宽度不变直接返回
        if (colWidth === this.props.colWidths[col]) {
            return;
        }

        const newColWidths = [...this.props.colWidths];
        const changeWidth = colWidth - newColWidths[col];
        const isRightEdge = col === newColWidths.length - 1;
        // 变更的列信息
        newColWidths[col] = newColWidths[col] + changeWidth;
        if (!isLeftEdge && !isRightEdge) {
            // 右侧列适配缩放，以达到当前列+右侧列的总宽度不变
            newColWidths[col + 1] = newColWidths[col + 1] - changeWidth;
        }
        const style: any = {};
        // 最左边缘拖拽，表格width以及left都发生变化
        if (isLeftEdge) {
            style.width = this.props.style.width + changeWidth;
            style.left = this.props.style.left - changeWidth;
        }
        // 最右边缘拖拽，表格width发生变化
        if (isRightEdge) {
            style.width = this.props.style.width + changeWidth;
        }
        const sdkDataV3 = this.sdkData as unknown as ITableDataModel;
        sdkDataV3.api.transaction(() => {
            const uiData = { props: { style, colWidths: newColWidths } };
            const sdkData: DeltaOf<ITableData> = {};
            TablePropertyConverter.convertPartialDataFromUiToSdk(uiData, sdkData, this);
            const transform = sdkData.spPr?.xfrm;
            const widths = sdkData.graphic?.graphicData?.tbl?.tblGrid?.gridCol
                ?.map((widthObj, index) => ({ index, width: widthObj.w }))
                ?.filter(e => e.width !== undefined);
            if (transform) {
                sdkDataV3.api.updateTransform(transform);
            }
            if (widths) {
                sdkDataV3.api.setTableColWidths(widths as IWidthInfo[]);
            }
        });
    };

    public resizeRow = (row: number, newSize: number, isTopEdge: boolean): void => {
        this.uploadEditingCellData(false);
        const rowHeight = Math.round(newSize);
        const oldRowHeight = this.displayRowHeights[row];
        // 高度不变直接返回
        if (rowHeight === oldRowHeight) {
            return;
        }

        const newRowHeights: (number | undefined)[] = Array.from({ length: this.props.rowHeights.length });
        newRowHeights[row] = rowHeight;

        const style: any = {
            height: this.props.style.height + rowHeight - oldRowHeight,
        };
        if (isTopEdge) {
            style.top = this.props.style.top - (rowHeight - oldRowHeight);
        }
        const sdkDataV3 = this.sdkData as unknown as ITableDataModel;
        sdkDataV3.api.transaction(() => {
            const uiData = { props: { style, rowHeights: newRowHeights } };
            const sdkData: DeltaOf<ITableData> = {};
            TablePropertyConverter.convertPartialDataFromUiToSdk(uiData, sdkData, this);
            const transform = sdkData.spPr?.xfrm;
            const heights = sdkData.graphic?.graphicData?.tbl?.tr
                ?.map((obj, index) => ({ index, height: obj?.h }))
                ?.filter(e => e.height !== undefined);
            if (transform) {
                sdkDataV3.api.updateTransform(transform);
            }
            if (heights) {
                sdkDataV3.api.setTableRowHeights(heights as IHeightInfo[]);
            }
        });
    };

    public resizeDisplayRowHeight = (): void => {
        mobx.transaction(() => {
            this.updateTableInfo(true);
        });
    };

    // mode - src/framework/editor/src/js/constants/index.js
    public resizeTable = (frame: IFrame, mode: string): IResizeInfo | undefined => {
        this.uploadEditingCellData(false);

        const oldSize = {
            width: this.props.style.width,
            height: this.props.style.height,
        };
        const colScale = frame.width / oldSize.width;
        const rowScale = frame.height / oldSize.height;

        mode = mode || '';

        // 大小没有变化直接返回
        if (colScale === 1 && rowScale === 1) {
            return;
        }

        const resizeInfo: IResizeInfo = {};

        if (colScale !== 1) {
            let tableWidth = 0;
            const colWidths: number[] = [];
            this.props.colWidths.forEach((width) => {
                const colWidth = Math.max(width * colScale, minColWidth);
                tableWidth += colWidth;
                colWidths.push(colWidth);
            });
            _set(resizeInfo, 'style.width', tableWidth);
            _set(resizeInfo, 'colWidths', colWidths);

            if (mode.indexOf('LEFT') !== -1 && resizeInfo.style) {
                resizeInfo.style.left = this.props.style.left - (resizeInfo.style.width! - oldSize.width);
            }
        }

        if (rowScale !== 1) {
            let tableHeight = 0;
            const rowHeights: number[] = [];
            this.props.rowHeights.forEach((height, i) => {
                const displayHeight = this.displayRowHeights[i];
                const rowHeight = Math.max(Math.round(displayHeight * rowScale), 10);
                tableHeight += rowHeight;
                rowHeights.push(rowHeight);
            });
            _set(resizeInfo, 'style.height', tableHeight);
            _set(resizeInfo, 'rowHeights', rowHeights);

            if (mode.indexOf('TOP') !== -1 && resizeInfo.style) {
                resizeInfo.style.top = this.props.style.top - (resizeInfo.style.height! - oldSize.height);
            }
        }

        return resizeInfo;
    };

    public getMergedCellRangeForCoords = (row: number, col: number): IRowColStartEnd => {
        for (let rowStart = Math.min(row, this.props.rowHeights.length - 1); rowStart >= 0; rowStart--) {
            const rowInfo = this.props.items[rowStart];
            for (let colStart = Math.min(col, this.props.colWidths.length - 1); colStart >= 0; colStart--) {
                const shape = rowInfo[colStart];
                if (shape && !shape.vMerge && !shape.hMerge) {
                    const rowEnd = rowStart + shape.rowspan - 1;
                    const colEnd = colStart + shape.colspan - 1;
                    if (rowEnd >= row && colEnd >= col) {
                        const range = {
                            rowStart,
                            colStart,
                            rowEnd,
                            colEnd,
                        };
                        return range;
                    }
                }
            }
        }
        return {
            rowStart: row,
            colStart: col,
            rowEnd: row,
            colEnd: col,
        };
    };

    public uploadEditingCellData = (needTriggerBlur: boolean): void => {
        this.props.items?.forEach((rowInfo) => {
            rowInfo?.forEach((shape) => {
                shape && this.uploadShapeData(shape, needTriggerBlur);
            });
        });
    };

    /**
     * 3.0表格文本的编辑从updateProps中独立出来, 根据uiCell二维数组更新数据
     * @param items
     */
    public updateTableAllParagraphList({ items }): void {
        const sdkDataV3 = this.sdkData as unknown as ITableDataModel;
        sdkDataV3.api.transaction(() => {
            items.forEach((row, rowIndex) => {
                row.forEach((cell, colIndex) => {
                    if (cell?.paragraphList) {
                        const submitCellSdkData: ICTTextBody = {};
                        TextConverter.convertPartialDataFromUiToSdk(cell, submitCellSdkData);
                        TableModel.sdkV3UpdateCellParagraphList(
                            this.sdkData as unknown as TableDataModel,
                            submitCellSdkData,
                            colIndex,
                            rowIndex,
                        );
                    }
                });
            });
        });
    }

    public uploadAllCellData = () => {
        let hasChange = false;
        const items: any = [];
        this.props.items.forEach((rowInfo) => {
            const tcList: (ITableCellModel | undefined)[] = [];
            rowInfo.forEach((shape) => {
                // 非合并的单元格才处理数据
                const isSingleCell = !shape.hMerge && !shape.vMerge;
                if (isSingleCell) {
                    const uiModel: any = {
                        paragraphList: shape.paragraphList,
                        textBodyStyle: shape.textBodyStyle,
                    };
                    if (uiModel) {
                        hasChange = true;
                    }
                    tcList.push(uiModel);
                } else {
                    tcList.push(undefined);
                }
            });
            items.push(tcList);
        });

        if (hasChange) {
            // 3.0表格文本的编辑从updateProps中独立出来
            this.updateTableAllParagraphList({ items });
        }
    };

    public uploadSelectedCellData = (
        editingCellData?: ITableCellModel,
        editingRow?: number,
        editingCol?: number,
    ): void => {
        if (!this.validUpload(editingCellData, editingRow, editingCol)) return;

        let hasChange = false;
        const items: any = [];
        this.props.items.forEach((rowInfo) => {
            const tcList: (ITableCellModel | undefined)[] = [];
            rowInfo.forEach((shape) => {
                // 非合并的单元格才处理数据
                const oldSelectedState = !shape.hMerge && !shape.vMerge
                && this.isCellSelected(shape, editingRow, editingCol);
                if (oldSelectedState) {
                    let uiModel;
                    if (shape.editorInstance) {
                        uiModel = editingCellData || shape.editorInstance.getData?.(true);
                    }
                    if (uiModel) {
                        hasChange = true;
                    }
                    tcList.push(uiModel);
                } else {
                    tcList.push(undefined);
                }
            });
            items.push(tcList);
        });

        if (hasChange) {
            // 3.0表格文本的编辑从updateProps中独立出来
            this.updateTableAllParagraphList({ items });
        }
    };

    public cellSelectedState = (
        row: number,
        col: number,
        selectRange?: IRowColStartEnd | null,
    ): boolean => {
        if (!selectRange) {
            return false;
        }
        const { rowStart, colStart, rowEnd, colEnd } = selectRange;
        const rowInfo = this.props.items && this.props.items.length > row ? this.props.items[row] : null;
        const shape = rowInfo && rowInfo.length > col && rowInfo[col];
        if (shape) {
            const rowspan = this.getSafeSpan(shape.rowspan);
            const colspan = this.getSafeSpan(shape.colspan);
            return !(row > rowEnd || row + rowspan <= rowStart || col > colEnd || col + colspan <= colStart);
        }
        return false;
    };

    public triggerBlur(): void {
        if (this.isSelectedOnlyOneCell() && !this.showSelection && this.selectedRange) {
            const { rowStart, colStart } = this.selectedRange;
            const shape = this.tableItems[rowStart][colStart];
            shape.editorInstance?.triggerBlur?.();
        }
    }

    public afterSelection = (rowStart: number, colStart: number, rowEnd: number, colEnd: number): void => {
        mobx.transaction(() => {
            const range = {
                rowStart: Math.min(rowStart, rowEnd),
                colStart: Math.min(colStart, colEnd),
                rowEnd: Math.max(rowStart, rowEnd),
                colEnd: Math.max(colStart, colEnd),
            };

            const selectedRange = this.calculatSelectedRange(range);
            const selectedCells = this.calculatSelectedCells(selectedRange);
            const selectionChanged = selectedCells.length !== this.selectedCellRanges.length
                || !this.rangeEqual(this.selectedCellRanges[0], selectedCells[0]);
            // 选中态发生变化或者选中范围变化，需要上传正处于编辑态的单元格数据
            // 判断原有选中长度已经新选中长度，精准命中从单个单元格编辑态到多选单元格的场景
            if ((this.showSelection || selectionChanged)
                && (selectedCells.length > 1 && this.selectedCellRanges.length === 1)) {
                this.uploadEditingCellData(true);
            }
            this.selectedCellRanges = selectedCells;
            // 变更选中区域
            this.selectedRange = selectedRange;
            if (selectionChanged) {
                // 重新计算itemvalue
                TableRefreshMenu.caculateItemValues(this);
            }
        });
    };

    public selectNextCell = (): void => {
        if (this.selectionService && this.selectionService.selectedElements.length > 1) {
            return;
        }
        const nextCellCoordinate = this.getNextCellCoordinate();
        const { nextRow, nextCol } = nextCellCoordinate;

        this.autoFocus = true;
        this.showSelection = false;
        if (nextRow === -1 && nextCol === -1) {
            this.uploadEditingCellData(true);
            const { rowEnd = 0 } = this.selectedRange || {};
            this.selectedCellRanges = [];
            this.selectedRange = null;
            this.defaultSelectedRange = {
                rowStart: rowEnd + 1,
                colStart: 0,
                rowEnd: rowEnd + 1,
                colEnd: 0,
            };
            (this.sdkData as unknown as ITableDataModel).api
                .insertRowsOrColumns(rowEnd + 1, 1, InsertDirection.BELOW);
        } else {
            this.afterSelection(nextRow, nextCol, nextRow, nextCol);
        }
        this.autoFocusSelectedCell();
    };

    public enterFirstSelectedCell = (e?: Event, autoFocusAfterClear = true): boolean => {
        // 有处于选中态的单元格时，enter后第一个单元格进入编辑态
        if ((this.selectionService && this.selectionService.selectedElements.length > 1)
            || !this.selectedCellRanges
            || this.selectedCellRanges.length <= 0) {
            return false;
        }
        const currentCellRange = this.selectedCellRanges[0];
        const enterRow = currentCellRange.rowStart;
        const enterCol = currentCellRange.colStart;
        const shape = this.props.items[enterRow][enterCol];
        if (!shape?.editorInstance?.isEditing) {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            this.showSelection = false;
            this.autoFocus = true;
            if (autoFocusAfterClear) {
                this.afterSelection(enterRow, enterCol, enterRow, enterCol);
                this.autoFocusSelectedCell();
            }
        }
        return true;
    };

    public getEditingCellModel = (): IShapeModel | null => {
        if (this.isSelectedOnlyOneCell()) {
            const selectedCell = this.selectedCellRanges[0];
            if (this.props.items && this.props.items.length > selectedCell.rowStart) {
                const rowInfo = this.props.items[selectedCell.rowStart];
                if (rowInfo && rowInfo.length > selectedCell.colStart) {
                    const shape = rowInfo[selectedCell.colStart];
                    return shape;
                }
            }
        }
        return null;
    };

    public getEditingElement = (): ITextEditor | null => {
        if (this.isSelectedOnlyOneCell()) {
            const selectedCell = this.selectedCellRanges[0];
            if (this.props.items && this.props.items.length > selectedCell.rowStart) {
                const rowInfo = this.props.items[selectedCell.rowStart];
                if (rowInfo && rowInfo.length > selectedCell.colStart) {
                    const shape = rowInfo[selectedCell.colStart];
                    if (shape.editorInstance?.isEditing) {
                        return shape.editorInstance;
                    }
                }
            }
        }
        return null;
    };

    public clearSelectedCellContent = (autoFocusAfterClear = true): boolean => {
        const promiseArr: any[] = [];
        if (this.showSelection && this.selectedCellRanges !== null) {
            for (let i = 0; i < this.selectedCellRanges.length; i++) {
                const cellRange = this.selectedCellRanges[i];
                const shapeModel = this.props.items[cellRange.rowStart][cellRange.colStart];
                shapeModel.parent = this.parent;
                const result = shapeModel.editorInstance?.emptyParagraph();
                if (result?.then) {
                    promiseArr.push(result);
                }
            }

            Promise.all(promiseArr).then(() => {
                this.uploadSelectedCellData();
                this.enterFirstSelectedCell(undefined, autoFocusAfterClear);
            });
            return true;
        }
        return false;
    };

    // eslint-disable-next-line complexity
    public paste = (data): boolean => {
        const shapeType = DataType.Shape;
        const tableType = DataType.GraphicalObjectFrameTable;
        if (data.type === shapeType) {
            let hasChange = false;
            const tblRowList: any[] = [];
            this.props.items.forEach((rowInfo, row) => {
                const tcList: (ICTTableCell | undefined)[] = [];
                rowInfo.forEach((shape, col) => {
                    // 非合并的单元格才处理数据
                    const oldSelectedState = !shape.hMerge && !shape.vMerge
                        && this.cellSelectedState(row, col, this.selectedRange);
                    if (oldSelectedState) {
                        const sdkCell = { [textBodyPath]: data[textBodyPath] };
                        tcList.push(sdkCell as unknown as ICTTableCell);
                        hasChange = true;
                    } else {
                        tcList.push(undefined);
                    }
                });
                const sdkRow = { [tableRowListColPath]: tcList };
                tblRowList.push(sdkRow);
            });

            if (hasChange) {
                const sdkDataV3 = this.sdkData as unknown as ITableDataModel;
                sdkDataV3.api.transaction(() => {
                    tblRowList.forEach((row, rowIndex) => {
                        row.tc.forEach((col, colIndex) => {
                            if (col?.[textBodyPath]) {
                                TableModel.sdkV3UpdateCellParagraphList(
                                    this.sdkData as unknown as TableDataModel,
                                    col[textBodyPath],
                                    colIndex,
                                    rowIndex,
                                );
                            }
                        });
                    });
                });
            }
            if (this.isEditing) {
                this.editorInstance?.setSelectionToEnd?.();
            }
            return true;
        } if (data.type === tableType) {
            if (this.selectedRange) {
                let canPaste = true;
                let hasChange = false;
                const { isEditing } = this;
                const pasteCellRange = this.selectedRange;
                let rowEnd = 0; let colEnd = 0;
                const tblRowList: any[] = [];
                this.props.items.forEach((rowInfo, row) => {
                    const tcList: (ICTTableCell | undefined)[] = [];
                    const sdkRow = { [tableRowListColPath]: tcList };
                    tblRowList.push(sdkRow);
                    rowInfo.forEach((shape, col) => {
                        tcList.push(undefined);
                        // 非合并的单元格才处理数据
                        const oldSelectedState = this.cellSelectedState(row, col, pasteCellRange);
                        if (isEditing || oldSelectedState) {
                            const copyRowIndex = row - pasteCellRange.rowStart;
                            const copyColIndex = col - pasteCellRange.colStart;
                            if (copyRowIndex >= 0 && copyRowIndex < _get(data, tableRowListPath).length
                                && copyColIndex >= 0 && copyColIndex < _get(data, tableColListPath).length) {
                                const cellData = _get(
                                    data,
                                    tableRowListPath,
                                )[copyRowIndex][tableRowListColPath][copyColIndex];
                                // 复制粘贴时，单元格 id 无需覆盖
                                delete cellData.id;

                                // rowSpan / gridSpan 处理，超出目标表格的部分做截断处理
                                this.handlePasteCellSpan(row, col, cellData);
                                tblRowList[row][tableRowListColPath][col] = cellData;

                                hasChange = true;
                                if (this.isMergeCell(shape)) {
                                    canPaste = false;
                                }
                                // 记录粘贴区域
                                if (row > rowEnd) {
                                    rowEnd = row;
                                }
                                if (col > colEnd) {
                                    colEnd = col;
                                }
                            }
                        }
                    });
                });

                if (canPaste && hasChange) {
                    const tableSdkData = this.sdkData as unknown as TableDataModel;
                    tableSdkData.api.transaction(() => {
                        tblRowList.forEach((row, rowIndex) => {
                            row.tc.forEach((cell, colIndex) => {
                                if (!cell) {
                                    return;
                                }
                                TableModel.sdkV3UpdateCellParagraphList(
                                    tableSdkData,
                                    cell.txBody,
                                    colIndex,
                                    rowIndex,
                                );
                                const range = {
                                    startRowIndex: rowIndex,
                                    startColIndex: colIndex,
                                    rowSpan: 1,
                                    colSpan: 1,
                                };
                                changeArrAppendToSetOrModify(cell.tcPr);
                                tableSdkData.api.updateTableCellProperties(cell.tcPr, range);
                            });
                        });
                    });
                    if (isEditing) {
                        this.showSelection = true;
                        this.afterSelection(pasteCellRange.rowStart, pasteCellRange.colStart, rowEnd, colEnd);
                    }
                }

                return canPaste;
            }
            return false;
        }
        return false;
    };

    public changesSaved(): boolean {
        if (this.isSelectedOnlyOneCell()) {
            const selectedCell = this.selectedCellRanges[0];
            if (this.props.items && this.props.items.length > selectedCell.rowStart) {
                const rowInfo = this.props.items[selectedCell.rowStart];
                if (rowInfo && rowInfo.length > selectedCell.colStart) {
                    const shape = rowInfo[selectedCell.colStart];
                    if (shape.editorInstance?.changeFlag) {
                        return false;
                    }
                }
            }
        }
        return true;
    }

    public selectAll = (): boolean => {
        if (this.showSelection) {
            this.afterSelection(0, 0, this.props.rowHeights.length - 1, this.props.colWidths.length - 1);
            return true;
        }
        return false;
    };

    // 计算合并后的选中单元格并保存
    public calculatSelectedCells = (selectedRange: IRowColStartEnd | null): IRowColStartEnd[] => {
        const cellRanges: IRowColStartEnd[] = [];
        if (selectedRange) {
            for (let row = selectedRange.rowStart; row <= selectedRange.rowEnd; row++) {
                for (let col = selectedRange.colStart; col <= selectedRange.colEnd; col++) {
                    if (!this.cellCoordsInSelectedRanges(row, col, cellRanges)) {
                        const cellRange = this.getMergedCellRangeForCoords(row, col);
                        cellRanges.push(cellRange);
                    }
                }
            }
        }
        return cellRanges;
    };

    public updateTableInfo = (forceUpdate?: boolean): void => {
        const width = this.measureTableWidth();
        const height = this.measureTableHeight(forceUpdate);
        // 更新表格边框信息
        this.collectTableBorders();

        if (this.props.style.width !== width) {
            this.props.style.width = width;
        }

        if (this.props.style.height !== height) {
            this.props.style.height = height;
        }

        this.changeSetEngine.notifyChange();
    };

    public cellCoordsInSelectedRanges = (row: number, col: number, selectedCellRanges: IRowColStartEnd[]): boolean => {
        for (let i = 0; i < selectedCellRanges.length; i++) {
            const {
                rowStart, colStart, rowEnd, colEnd,
            } = selectedCellRanges[i];
            if (row <= rowEnd && row >= rowStart && col <= colEnd && col >= colStart) {
                return true;
            }
        }
        return false;
    };

    public getBorder(): ITableBorderProperty {
        if (!this.tableBorderInstance) {
            this.tableBorderInstance = new TableBorder(this);
        }
        return this.tableBorderInstance;
    }

    public selectOneMergedCell = (): boolean => {
        let selectOneMergedCell = false;
        if (this.isSelectedOnlyOneCell()) {
            const range = this.selectedCellRanges[0];
            const cellData = this.props.items[range.rowStart][range.colStart];
            if (cellData.rowspan > 1 || cellData.colspan > 1) {
                selectOneMergedCell = true;
            }
        }
        return selectOneMergedCell;
    };

    public canMergeSelectedCells = (): boolean => {
        if (!this.selectedCellRanges || this.selectedCellRanges.length <= 0) {
            return false;
        }
        let [{ rowStart, colStart, rowEnd, colEnd }] = this.selectedCellRanges;
        // 计算所选范围的最大包络矩形
        this.selectedCellRanges.forEach((range) => {
            rowStart = Math.min(rowStart, range.rowStart);
            colStart = Math.min(colStart, range.colStart);
            rowEnd = Math.max(rowEnd, range.rowEnd);
            colEnd = Math.max(colEnd, range.colEnd);
        });
        // 所选区域和最大包络矩形一致的时候才可合并
        const totalCellsCount = (colEnd - colStart + 1) * (rowEnd - rowStart + 1);
        const actualCellsCount = this.selectedCellRanges.reduce((count, { rowStart, colStart, rowEnd, colEnd }) => {
            const width = rowEnd - rowStart + 1;
            const height = colEnd - colStart + 1;
            return (width * height) + count;
        }, 0);
        return totalCellsCount > 1 && actualCellsCount === totalCellsCount;
    };

    public handleInsertRowTop = (insertRange: IInsertRange) => {
        this.uploadEditingCellData(true);
        this.insertRowAbove(insertRange);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TABLE_FORMAT,
            action: TDWActionType.TABLE_INSERT_ROW,
        });
        return true;
    };
    public handleInsertRowBottom = (insertRange: IInsertRange) => {
        this.uploadEditingCellData(true);
        this.insertRowBelow(insertRange);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TABLE_FORMAT,
            action: TDWActionType.TABLE_INSERT_ROW,
        });
        return true;
    };
    public handleInsertColLeft = (insertRange: IInsertRange) => {
        this.uploadEditingCellData(true);
        this.insertColumnLeft(insertRange);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TABLE_FORMAT,
            action: TDWActionType.TABLE_INSERT_COLUMN,
        });
        return true;
    };
    public handleInsertColRight = (insertRange: IInsertRange) => {
        this.uploadEditingCellData(true);
        this.insertColumnRight(insertRange);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TABLE_FORMAT,
            action: TDWActionType.TABLE_INSERT_COLUMN,
        });
        return true;
    };
    public handleDeleteRow = () => {
        this.uploadEditingCellData(true);
        this.deleteSelectedRow();
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TABLE_FORMAT,
            action: TDWActionType.TABLE_DELETE_ROW,
        });
        return true;
    };
    public handleDeleteCol = () => {
        this.uploadEditingCellData(true);
        this.deleteSelectedCol();
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TABLE_FORMAT,
            action: TDWActionType.TABLE_DELETE_COLUMN,
        });
        return true;
    };
    public handleDeleteTable = () => {
        this.uploadEditingCellData(true);
        this.deleteTable();
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TABLE_FORMAT,
            action: TDWActionType.TABLE_DELETE,
        });
        return true;
    };
    public handleMergeCell = () => {
        this.uploadEditingCellData(true);
        if (this.selectOneMergedCell()) {
            this.cancelMergedCell();
        } else {
            this.mergeSelectedCells();
        }
        return true;
    };

    public getUpdateState(): {} {
        const cellStateMap = {};
        this.props.items.forEach((row) => {
            row.forEach((cell) => {
                cellStateMap[cell.key] = cloneDeep(cell.updateState);
            });
        });
        return {
            cellStateMap,
            tableState: { shouldUpdate: this.shouldUpdate },
        };
    }

    public resetUpdateState() {
        this.shouldUpdate = false;
    }

    public calculatSelectedRange = (range: IRowColStartEnd): IRowColStartEnd => {
        if (range) {
            const selectedRange = {
                rowStart: 0,
                colStart: 0,
                rowEnd: this.props.rowHeights.length - 1,
                colEnd: this.props.colWidths.length - 1,
            };
            for (let row = range.rowStart; row <= range.rowEnd; row++) {
                // 左边
                {
                    const mergedCellRange = this.getMergedCellRangeForCoords(row, range.colStart);
                    selectedRange.colStart = Math.max(selectedRange.colStart, mergedCellRange.colStart);
                }
                // 右边
                {
                    const mergedCellRange = this.getMergedCellRangeForCoords(row, range.colEnd);
                    selectedRange.colEnd = Math.min(selectedRange.colEnd, mergedCellRange.colEnd);
                }
            }
            for (let col = range.colStart; col <= range.colEnd; col++) {
                // 上边
                {
                    const mergedCellRange = this.getMergedCellRangeForCoords(range.rowStart, col);
                    selectedRange.rowStart = Math.max(selectedRange.rowStart, mergedCellRange.rowStart);
                }
                // 底边
                {
                    const mergedCellRange = this.getMergedCellRangeForCoords(range.rowEnd, col);
                    selectedRange.rowEnd = Math.min(selectedRange.rowEnd, mergedCellRange.rowEnd);
                }
            }
            return selectedRange;
        }
        return range;
    };

    /**
     * 计算行高
     * @param row
     * @private
     */
    public measureRowCellsHeight(row: number): number[] {
        const rowMeasureContents: (IBatchMeasureBlockSizeProps | null)[] = [];
        const rowList = this.props.items[row];
        rowList.forEach((shape, col) => {
            // 是否需要计算文本高度
            const { cellWidthChanged } = shape;
            _set(shape, 'cellWidthChanged', false);

            const textBodyPathStr = `${tableRowListPath}.${row}.${tableRowListColPath}.${col}.${textBodyPath}`;
            const textStylePath = `${tableRowListPath}.${row}.${tableRowListColPath}.${col}.tcPr.anchor`;
            const cellStylePath = `${tableRowListPath}.${row}.${tableRowListColPath}.${col}.tcPr`;

            const textBodyChanged = this.changeSetEngine.hasChangeAtPath(textBodyPathStr);
            // 3.0的垂直对齐方式变更体现在textBodyPathStr上，无需用textStylePath
            const textStyleChanged = textBodyChanged;
            const cellStyleChanged = this.changeSetEngine.hasChangeAtPath(cellStylePath);
            if (cellWidthChanged || textBodyChanged || textStyleChanged) {
                rowMeasureContents.push({
                    width: shape.props.style.width,
                    paragraphList: mobx.toJS(shape.paragraphList),
                    textBodyStyle: mobx.toJS(shape.textBodyStyle),
                });
            } else {
                rowMeasureContents.push(null);
            }

            if (textBodyChanged || textStyleChanged) {
                shape.setUpdateState({ shouldUpdate: true });
                _set(shape, 'state.dataChanged', true);
                shape.saveState?.({ shouldUpdate: true });
            } else {
                shape.setUpdateState({ shouldUpdate: false });
            }
            if (textBodyChanged || cellStyleChanged) {
                _set(this, 'itemValueUpdate', true);
            }
        });

        // 计算文本高度
        const option = {
            slideNum: getSlideIndex(this.slideRef) + 1,
            datetime: this.initDatetime,
        };
        return ResizeMeasureCanvas.batchGetEditorBlockSizes(rowMeasureContents, option);
    }

    protected cloneJsonObject() {
        const clone = { ...this };
        const items = this.props.items.map((row: IBaseModel[]) => row.map((item: IBaseModel) => {
            const cloneChild = item.clone();
            cloneChild.parent = clone;
            return cloneChild;
        }));
        clone.props = {
            colWidths: cloneDeep(this.props.colWidths),
            items,
            rowHeights: cloneDeep(this.props.rowHeights),
            style: cloneDeep(this.props.style),
        };

        delete clone.linkProperty;
        delete clone.frameInstance;
        delete clone.fillInstance;
        delete clone.effectInstance;
        delete clone.borderInstance;
        delete clone.property3dInstance;
        delete clone.lockInstance;
        delete clone._scene3d;
        delete clone._shape3d;
        return clone;
    }

    /**
     * 兜底
     * @param span
     */
    private getSafeSpan(span: number): number {
        return span || 1;
    }

    /**
     * 重置单元格高度
     * @param item 单元格model
     * @param col 列号
     * @param displayHeights
     * @private
     */
    private resetHeightByMeasuredValues(item: ITableCellModel, col: number, displayHeights: number[]): void {
        if (displayHeights[col] > 0) {
            item.displayHeight = Math.round(displayHeights[col]);
        }
    }

    /**
     * 计算表格宽度
     * @private
     */
    private measureTableWidth(): number {
        let width = 0;
        this.props.colWidths.forEach((colWidth) => {
            width += colWidth;
        });
        return width;
    }

    /**
     * 获取单元格高度
     * @param row
     * @param col
     * @private
     */
    private getCellHeightForRow(row: number, col: number): number {
        // 单元格最底部一行的行高自适应文本高度
        const mergedCellRange = this.getMergedCellRangeForCoords(row, col);
        if (mergedCellRange.rowEnd === row) {
            return this.cellLastRowHeight(row, col);
        }
        return 0;
    }

    /**
     * 计算表格高度
     * @param forceUpdate
     */
    private measureTableHeight(forceUpdate?: boolean): number {
        let height = 0;
        // 计算每一行的行高
        this.props.rowHeights.forEach((rowHeight, row) => {
            // 计算文本高度
            const displayHeights = forceUpdate ? [] : this.measureRowCellsHeight(row);

            let maxDisplayHeight = 0;
            const tcList = this.props.items[row];
            tcList.forEach((item, col) => {
                this.resetHeightByMeasuredValues(item, col, displayHeights);

                // 获取单元格在某一行内需要的高度
                const displayHeight = this.getCellHeightForRow(row, col);
                maxDisplayHeight = Math.max(maxDisplayHeight, displayHeight);
            });
            // 获取自适应后的最大高度
            const displayRowHeight = Math.round(Math.max(maxDisplayHeight, rowHeight));
            this.updateDisplayRowHeights(row, displayRowHeight);

            height += this.displayRowHeights[row];
        });
        return height;
    }

    /**
     * 更新显示的行高
     * @param row
     * @param displayRowHeight
     * @private
     */
    private updateDisplayRowHeights(row: number, displayRowHeight: number): void {
        if (row < 0) return;
        let needUpdate = false;
        if (row >= this.displayRowHeights.length) {
            needUpdate = true;
        } else if (this.displayRowHeights[row] !== displayRowHeight) {
            needUpdate = true;
        }

        if (needUpdate) {
            _set(this, 'shouldUpdate', true);
            this.saveState?.({ shouldUpdate: true });
            this.displayRowHeights[row] = displayRowHeight;
        }
    }

    private needCollectBorder(collect: ICollect, borders: IBorder[]): boolean {
        return (!borders[collect.key] || collect.level > borders[collect.key].level);
    }

    private collectTableBorder(frame: IFrame, collect: ICollect, borders: any): void {
        if (this.needCollectBorder(collect, borders)) {
            if (collect.info) {
                borders[collect.key] = {
                    key: collect.key,
                    ...frame,
                    position: 'absolute',
                    [collect.resetFrameKey]: collect.info?.border?.width || 1,
                    backgroundColor: collect.info?.border?.color || '#000000',
                    level: collect.level,
                    style: collect.info.border?.style,
                };
            } else {
                borders[collect.key] = {
                    key: collect.key,
                    ...frame,
                    position: 'absolute',
                    [collect.resetFrameKey]: 1,
                    backgroundColor: undefined,
                    level: collect.level,
                };
            }
        }
    }

    private collectTableBorderLeft(
        coordinate: IRowCol,
        cellFrame: IRect,
        mergedShape: ITableCellModel,
        mergedCellRange: IRowColStartEnd,
        borders: any,
    ): void {
        if (mergedCellRange.colStart === coordinate.col) {
            const borderFrame = {
                ...cellFrame,
            };
            const key = `v_border_row${coordinate.row}col${coordinate.col}`;
            this.collectTableBorder(borderFrame, {
                info: mergedShape?.borders?.left,
                key,
                level: mergedCellRange.rowStart,
                resetFrameKey: 'width',
            }, borders);
        }
    }
    private collectTableBorderTop(
        coordinate: IRowCol,
        cellFrame: IRect,
        mergedShape: ITableCellModel,
        mergedCellRange: IRowColStartEnd,
        borders: any,
    ): void {
        if (mergedCellRange.rowStart === coordinate.row) {
            const borderFrame = {
                ...cellFrame,
            };
            const key = `h_border_row${coordinate.row}col${coordinate.col}`;
            this.collectTableBorder(borderFrame, {
                info: mergedShape?.borders?.top,
                key,
                level: mergedCellRange.colStart,
                resetFrameKey: 'height',
            }, borders);
        }
    }
    private collectTableBorderRight(
        coordinate: IRowCol,
        cellFrame: IRect,
        mergedShape: ITableCellModel,
        mergedCellRange: IRowColStartEnd,
        borders: any,
    ): void {
        if (mergedCellRange.colEnd === coordinate.col) {
            const borderFrame = {
                ...cellFrame,
                left: cellFrame.left + cellFrame.width,
            };
            const key = `v_border_row${coordinate.row}col${coordinate.col + 1}`;
            this.collectTableBorder(borderFrame, {
                info: mergedShape?.borders?.right,
                key,
                level: mergedCellRange.rowStart,
                resetFrameKey: 'width',
            }, borders);
        }
    }
    private collectTableBorderBottom(
        coordinate: IRowCol,
        cellFrame: IRect,
        mergedShape: ITableCellModel,
        mergedCellRange: IRowColStartEnd,
        borders: any,
    ): void {
        if (mergedCellRange.rowEnd === coordinate.row) {
            const borderFrame = {
                ...cellFrame,
                top: cellFrame.top + cellFrame.height,
            };
            const key = `h_border_row${coordinate.row + 1}col${coordinate.col}`;
            this.collectTableBorder(borderFrame, {
                info: mergedShape?.borders?.bottom,
                key,
                level: mergedCellRange.colStart,
                resetFrameKey: 'height',
            }, borders);
        }
    }

    private collectTableBorders(): void {
        const borders = {};
        let left = 0;
        let top = 0;
        this.props.items.forEach((tcList, row) => {
            const rowHeight = this.displayRowHeights[row];
            left = 0;
            tcList.forEach((item, col) => {
                const shape = item;
                const colWidth = this.props.colWidths[col];
                shape.props.style.height = this.cellHeight(row, shape.rowspan);
                shape.props.style.left = left;
                shape.props.style.top = top;

                const mergedCellRange = this.getMergedCellRangeForCoords(row, col);
                const mergedShape = this.props.items[mergedCellRange.rowStart][mergedCellRange.colStart];
                if (mergedShape.borders) {
                    const coordinate = { row, col };
                    const cellFrame = {
                        left,
                        top,
                        width: colWidth,
                        height: rowHeight,
                    };
                    this.collectTableBorderLeft(coordinate, cellFrame, mergedShape, mergedCellRange, borders);
                    this.collectTableBorderRight(coordinate, cellFrame, mergedShape, mergedCellRange, borders);
                    this.collectTableBorderTop(coordinate, cellFrame, mergedShape, mergedCellRange, borders);
                    this.collectTableBorderBottom(coordinate, cellFrame, mergedShape, mergedCellRange, borders);
                }
                left += colWidth;
            });
            top += rowHeight;
        });
        this.tableBorders = borders;
    }

    private cellLastRowHeight = (row: number, col: number): number => {
        const cellRange = this.getMergedCellRangeForCoords(row, col);
        const shape = this.props.items[cellRange.rowStart][cellRange.colStart];
        let lastLineHeight = shape.displayHeight || 10;
        for (let r = cellRange.rowStart; r < cellRange.rowEnd; r++) {
            lastLineHeight -= this.displayRowHeights[r];
        }
        return lastLineHeight;
    };

    private isSelectedOnlyOneCell = (): boolean => {
        if (this.selectedCellRanges.length === 1) {
            return true;
        }
        return false;
    };

    private getCustomInsetRange = (insertRange: IInsertRange) => {
        const { colStart = 0, colEnd = 0, rowStart = 0, rowEnd = 0 } = this.selectedRange || {};
        const selectedHeight = rowEnd - rowStart;
        const selectedWidth = colEnd - colStart;
        const insertHeight = insertRange.rowSpan;
        const insertWidth = insertRange.colSpan;
        const x = colStart - insertWidth + (selectedWidth + 1);
        const y = rowStart - insertHeight + (selectedHeight + 1);
        return {
            x,
            y,
            width: insertWidth,
            height: insertHeight,
        };
    };

    private getInsertRange = () => {
        const { colStart = 0, colEnd = 0, rowStart = 0, rowEnd = 0 } = this.selectedRange || {};
        return {
            x: colStart,
            y: rowStart,
            width: colEnd - colStart + 1,
            height: rowEnd - rowStart + 1,
        };
    };

    private insertRowBelow = (insertRange): void => {
        const { rowEnd = 0 } = this.selectedRange || {};
        this.showSelection = true;
        this.selectedCellRanges = [];
        this.selectedRange = null;
        this.defaultSelectedRange = {
            rowStart: rowEnd + 1,
            colStart: 0,
            rowEnd: rowEnd + 1 + insertRange.rowSpan - 1,
            colEnd: this.props.colWidths.length - 1,
        };
        (this.sdkData as unknown as ITableDataModel).api
            .insertRowsOrColumns(rowEnd + 1, insertRange.rowSpan, InsertDirection.BELOW);
    };

    private insertRowAbove = (insertRange: IInsertRange): void => {
        const { rowStart = 0 } = this.selectedRange || {};
        this.showSelection = true;
        this.selectedCellRanges = [];
        this.selectedRange = null;
        this.defaultSelectedRange = {
            rowStart,
            colStart: 0,
            rowEnd: rowStart + insertRange.rowSpan - 1,
            colEnd: this.props.colWidths.length - 1,
        };
        (this.sdkData as unknown as ITableDataModel).api
            .insertRowsOrColumns(rowStart, insertRange.rowSpan, InsertDirection.ABOVE);
    };

    private insertColumnLeft = (insertRange): void => {
        const { colStart = 0 } = this.selectedRange || {};
        this.showSelection = true;
        this.selectedCellRanges = [];
        this.selectedRange = null;
        this.defaultSelectedRange = {
            rowStart: 0,
            colStart,
            rowEnd: this.props.rowHeights.length - 1,
            colEnd: colStart + insertRange.colSpan - 1,
        };
        (this.sdkData as unknown as ITableDataModel).api
            .insertRowsOrColumns(colStart, insertRange.colSpan, InsertDirection.LEFT);
    };

    private insertColumnRight = (insertRange): void => {
        const { colEnd = 0 } = this.selectedRange || {};
        this.showSelection = true;
        this.selectedCellRanges = [];
        this.selectedRange = null;
        this.defaultSelectedRange = {
            rowStart: 0,
            colStart: colEnd + 1,
            rowEnd: this.props.rowHeights.length - 1,
            colEnd: colEnd + 1 + insertRange.colSpan - 1,
        };
        (this.sdkData as unknown as ITableDataModel).api
            .insertRowsOrColumns(colEnd + 1, insertRange.colSpan, InsertDirection.RIGHT);
    };

    private getDeleteRange = (): IRange => {
        let selectedCellRange = this.selectedRange;
        if (this.isSelectedOnlyOneCell()) {
            selectedCellRange = this.getMergedCellRangeForCoords(
                this.selectedRange?.rowStart || 0,
                this.selectedRange?.colStart || 0,
            );
        }
        const range = {
            x: selectedCellRange?.colStart || 0,
            y: selectedCellRange?.rowStart || 0,
            width: (selectedCellRange?.colEnd || 0) - (selectedCellRange?.colStart || 0) + 1,
            height: (selectedCellRange?.rowEnd || 0) - (selectedCellRange?.rowStart || 0) + 1,
        };
        return range;
    };

    private deleteSelectedRow = (): void => {
        const range = this.getDeleteRange();
        if (range.height === this.props.rowHeights.length) {
            this.slideRef?.deleteElement();
        } else {
            (this.sdkData as unknown as ITableDataModel).api
                .deleteRowsOrColumns(range.y, range.height, DeleteDirection.ROW);
        }
    };

    private deleteSelectedCol = (): void => {
        const range = this.getDeleteRange();
        if (range.width === this.props.colWidths.length) {
            this.slideRef?.deleteElement();
        } else {
            (this.sdkData as unknown as ITableDataModel).api
                .deleteRowsOrColumns(range.x, range.width, DeleteDirection.COLUMN);
        }
    };

    private deleteTable = (): void => {
        (this.parent as ISlideBaseModel)?.deleteElement();
    };

    private cancelMergedCell = (): void => {
        const selectedRange = this.selectedCellRanges[0];
        const range = {
            x: selectedRange.colStart,
            y: selectedRange.rowStart,
            width: selectedRange.colEnd - selectedRange.colStart + 1,
            height: selectedRange.rowEnd - selectedRange.rowStart + 1,
        };

        this.showSelection = true;
        this.selectedCellRanges = [];
        this.selectedRange = null;
        this.defaultSelectedRange = selectedRange;

        this.apiCancelMergedCell(range);
    };

    private getNewStartRange(startRange: IRowCol | null, range: IRowColStartEnd): IRowCol {
        if (!startRange || (range.rowStart <= startRange.row && range.colStart <= startRange.col)) {
            return {
                row: range.rowStart,
                col: range.colStart,
            };
        }
        return startRange;
    }

    private getNewEndRange(endRange: IRowCol | null, range: IRowColStartEnd): IRowCol {
        if (!endRange || (range.rowEnd >= endRange.row && range.colEnd >= endRange.col)) {
            return {
                row: range.rowEnd,
                col: range.colEnd,
            };
        }
        return endRange;
    }

    private selectedCellMergeRange = (): IRowColStartEnd => {
        let startCell;
        let endCell;
        this.selectedCellRanges.forEach((range) => {
            startCell = this.getNewStartRange(startCell, range);
            endCell = this.getNewEndRange(endCell, range);
        });
        return {
            rowStart: startCell.row,
            colStart: startCell.col,
            rowEnd: endCell.row,
            colEnd: endCell.col,
        };
    };
    private mergeSelectedCells = (): void => {
        const selectedRange = this.selectedCellMergeRange();
        const range = {
            x: selectedRange.colStart,
            y: selectedRange.rowStart,
            width: selectedRange.colEnd - selectedRange.colStart + 1,
            height: selectedRange.rowEnd - selectedRange.rowStart + 1,
        };
        this.apiMergeCell(range);
        this.showSelection = true;
    };

    private uploadShapeData(model: ITableCellModel, needTriggerBlur): void {
        // 在editor未进编辑态的情况下多选单元格，松手后editor获得焦点
        // 为了处理这个问题，这里通过shape.isEditing的状态将editor给blur
        const shape = model;
        if (shape.editorInstance && (shape.editorInstance.isEditing || shape.isEditing)) {
            if (needTriggerBlur) {
                shape.editorInstance?.triggerBlur?.();
                shape.isEditing = false;
            }
        }
    }

    private validUpload = (editingCellData, editingRow, editingCol): boolean => {
        // 表格内无选中的单元格
        if (!this.selectedRange) return false;
        // 已经主动传入 editingRow 或 editingCol 的值
        if (expect(editingRow).isNumber() && expect(editingCol).isNumber()) return true;
        // 表格内单元格处于编辑态,选中单元格超过1个
        if (editingCellData && !this.isSelectedOnlyOneCell()) return false;
        return true;
    };

    private isCellSelected(shapeModel: ITableCellModel, editingRow?: number, editingCol?: number): boolean {
        if (editingRow !== undefined && editingCol !== undefined) {
            return editingRow === shapeModel.row && editingCol === shapeModel.col;
        }
        if (this.selectedRange) {
            return this.cellSelectedState(shapeModel.row!, shapeModel.col!, this.selectedRange);
        }
        return false;
    }

    private rangeEqual = (range1: IRowColStartEnd, range2: IRowColStartEnd): boolean => {
        if (range1 === range2) return true;
        if (!range1 || !range2) return false;

        return (
            range1.rowStart === range2.rowStart
            && range1.rowEnd === range2.rowEnd
            && range1.colStart === range2.colStart
            && range1.colEnd === range2.colEnd
        );
    };

    private autoFocusSelectedCell = (): void => {
        if (!this.autoFocus) {
            return;
        }

        const selectOnlyOneCell = this.isSelectedOnlyOneCell();
        if (selectOnlyOneCell) {
            const cellRange = this.selectedCellRanges[0];
            const shape = this.props.items[cellRange.rowStart][cellRange.colStart];
            let cellEditState = false;
            if (!this.showSelection) {
                cellEditState = true;
            } else {
                cellEditState = false;
            }

            shape.isEditing = cellEditState;
            shape.editorInstance?.setFocus();
        }
    };

    private getNextCellCoordinate(): { nextRow: number, nextCol: number } {
        const currentCellRange = this.selectedCellRanges[0];
        let nextRow = -1;
        let nextCol = -1;
        if (!this.isSelectedOnlyOneCell()) {
            // 选中多个单元格，tab键切换到选中区域第一个格并进入编辑态
            nextRow = currentCellRange.rowStart;
            nextCol = currentCellRange.colStart;
        } else {
            for (let row = currentCellRange.rowStart; row < this.props.rowHeights.length; row++) {
                let col = row === currentCellRange.rowStart ? currentCellRange.colEnd + 1 : 0;
                for (; col < this.props.colWidths.length; col++) {
                    const shape = this.props.items[row][col];
                    if (!shape.vMerge && !shape.hMerge) {
                        nextRow = row;
                        nextCol = col;
                        break;
                    }
                }
                if (nextRow !== -1 && nextCol !== -1) {
                    break;
                }
            }
        }
        return {
            nextRow,
            nextCol,
        };
    }

    private isMergeCell = (shape): boolean => {
        if (shape.rowspan > 1 || shape.colspan > 1 || shape.hMerge || shape.vMerge) {
            return true;
        }
        return false;
    };

    private handlePasteCellSpan = (row, col, cellData) => {
        if (!this.selectedRange) {
            return;
        }
        const maxRowLength = this.showSelection
            ? Math.min(this.selectedRange.rowEnd + 1, this.props.rowHeights.length)
            : this.props.rowHeights.length;
        if (cellData.rowSpan && cellData.rowSpan > 1
            && row + cellData.rowSpan > maxRowLength) {
            _set(cellData, 'rowSpan', maxRowLength - row);
        }
        const maxColLength = this.showSelection
            ? Math.min(this.selectedRange.colEnd + 1, this.props.colWidths.length)
            : this.props.colWidths.length;
        if (cellData.gridSpan && cellData.gridSpan > 1
            && col + cellData.gridSpan > maxColLength) {
            _set(cellData, 'gridSpan', maxColLength - col);
        }
    };

    /**
     * 将2.0的range转换为3.0格式
     * @param range
     */
    private transformRangeToV3 = (range: IRange): Required<ITableRangeData> => ({
        startRowIndex: range.y,
        startColIndex: range.x,
        rowSpan: range.height,
        colSpan: range.width,
    });

    /**
     * 取消合并单元格
     * @param range
     */
    private apiCancelMergedCell = (range): void => {
        const rangeV3 = this.transformRangeToV3(range);
        (this.sdkData as unknown as ITableDataModel).api.cancelMergedCell(rangeV3);
    };

    /**
     * 合并单元格
     * @param range
     */
    private apiMergeCell = (range): void => {
        const rangeV3 = this.transformRangeToV3(range);
        if ((rangeV3.colSpan > 0 && rangeV3.rowSpan > 0) && (rangeV3.colSpan > 1 || rangeV3.rowSpan > 1)) {
            // 合并单元格的话，行数或者列数至少有一个要大于1
            (this.sdkData as unknown as ITableDataModel).api.mergeCell(rangeV3);
        }
    };
}

export default TableModel;
